/**
 * Copia del mapa de plantillas del Cloudflare Worker.
 *
 * Fuente de verdad: `cloudflare-worker-juaperrone/worker.js` → `COUNTRY_TEMPLATE_MAP` + `DEFAULT_CONFIG`.
 * Tras cambios en el Worker, actualizar este archivo y desplegar el front.
 *
 * Política actual:
 *  - AR   → BaseTemplate (provincia controla CTA)
 *  - Resto del mundo → USExclusiveLanding (fullscreen TikTok EN + Stripe; default global; override por país/estado/ciudad vía /admin/country-template)
 *  - Excepción: FL (estado US) → BaseTemplate vía fila locked.
 *
 * Para simular visitor US desde AR (sin VPN real): usa ?debug=...&country=US o el geo sim toolbar.
 * Esto fuerza country=US → USExclusiveLanding (lo que ven visitors US en prod).
 */

import type { ServerTemplateConfig } from "@/lib/serverTemplateDetection";

export type WorkerTemplateRow = {
  template: string;
  bioLayout: null;
  language: string;
};

/** Igual que `DEFAULT_CONFIG` en worker.js */
export const WORKER_DEFAULT_TEMPLATE: WorkerTemplateRow = {
  template: "USExclusiveLanding",
  bioLayout: null,
  language: "en-US",
};

/**
 * Igual que `COUNTRY_TEMPLATE_MAP` + `getARConfig` / `getUSConfig` en worker.js (sin rutas especiales como /sorprendeme).
 */
export const WORKER_COUNTRY_TEMPLATE_MAP: Record<string, WorkerTemplateRow> = {
  AE: { template: "USExclusiveLanding", bioLayout: null, language: "ar-AE" },
  AL: { template: "USExclusiveLanding", bioLayout: null, language: "sq-AL" },
  AM: { template: "USExclusiveLanding", bioLayout: null, language: "hy-AM" },
  AR: { template: "BaseTemplate", bioLayout: null, language: "es-AR" },
  AT: { template: "USExclusiveLanding", bioLayout: null, language: "de-AT" },
  AU: { template: "USExclusiveLanding", bioLayout: null, language: "en-AU" },
  AZ: { template: "USExclusiveLanding", bioLayout: null, language: "az-AZ" },
  BA: { template: "USExclusiveLanding", bioLayout: null, language: "bs-BA" },
  BD: { template: "USExclusiveLanding", bioLayout: null, language: "bn-BD" },
  BE: { template: "USExclusiveLanding", bioLayout: null, language: "fr-BE" },
  BG: { template: "USExclusiveLanding", bioLayout: null, language: "bg-BG" },
  BO: { template: "USExclusiveLanding", bioLayout: null, language: "es-BO" },
  BR: { template: "USExclusiveLanding", bioLayout: null, language: "pt-BR" },
  CA: { template: "USExclusiveLanding", bioLayout: null, language: "en-CA" },
  CH: { template: "USExclusiveLanding", bioLayout: null, language: "de-CH" },
  CL: { template: "USExclusiveLanding", bioLayout: null, language: "es-CL" },
  CN: { template: "USExclusiveLanding", bioLayout: null, language: "zh-CN" },
  CO: { template: "USExclusiveLanding", bioLayout: null, language: "es-CO" },
  CR: { template: "USExclusiveLanding", bioLayout: null, language: "es-CR" },
  CU: { template: "USExclusiveLanding", bioLayout: null, language: "es-CU" },
  CY: { template: "USExclusiveLanding", bioLayout: null, language: "el-CY" },
  CZ: { template: "USExclusiveLanding", bioLayout: null, language: "cs-CZ" },
  DE: { template: "USExclusiveLanding", bioLayout: null, language: "de-DE" },
  DK: { template: "USExclusiveLanding", bioLayout: null, language: "da-DK" },
  DO: { template: "USExclusiveLanding", bioLayout: null, language: "es-DO" },
  DZ: { template: "USExclusiveLanding", bioLayout: null, language: "ar-DZ" },
  EC: { template: "USExclusiveLanding", bioLayout: null, language: "es-EC" },
  EE: { template: "USExclusiveLanding", bioLayout: null, language: "et-EE" },
  EG: { template: "USExclusiveLanding", bioLayout: null, language: "ar-EG" },
  ES: { template: "USExclusiveLanding", bioLayout: null, language: "es-ES" },
  FI: { template: "USExclusiveLanding", bioLayout: null, language: "fi-FI" },
  FR: { template: "USExclusiveLanding", bioLayout: null, language: "fr-FR" },
  GB: { template: "USExclusiveLanding", bioLayout: null, language: "en-GB" },
  GE: { template: "USExclusiveLanding", bioLayout: null, language: "ka-GE" },
  GH: { template: "USExclusiveLanding", bioLayout: null, language: "en-GH" },
  GR: { template: "USExclusiveLanding", bioLayout: null, language: "el-GR" },
  GT: { template: "USExclusiveLanding", bioLayout: null, language: "es-GT" },
  HK: { template: "USExclusiveLanding", bioLayout: null, language: "zh-HK" },
  HN: { template: "USExclusiveLanding", bioLayout: null, language: "es-HN" },
  HR: { template: "USExclusiveLanding", bioLayout: null, language: "hr-HR" },
  HU: { template: "USExclusiveLanding", bioLayout: null, language: "hu-HU" },
  ID: { template: "USExclusiveLanding", bioLayout: null, language: "id-ID" },
  IE: { template: "USExclusiveLanding", bioLayout: null, language: "en-IE" },
  IL: { template: "USExclusiveLanding", bioLayout: null, language: "he-IL" },
  IN: { template: "USExclusiveLanding", bioLayout: null, language: "hi-IN" },
  IQ: { template: "USExclusiveLanding", bioLayout: null, language: "ar-IQ" },
  IS: { template: "USExclusiveLanding", bioLayout: null, language: "is-IS" },
  IT: { template: "USExclusiveLanding", bioLayout: null, language: "it-IT" },
  JM: { template: "USExclusiveLanding", bioLayout: null, language: "en-JM" },
  JO: { template: "USExclusiveLanding", bioLayout: null, language: "ar-JO" },
  JP: { template: "USExclusiveLanding", bioLayout: null, language: "ja-JP" },
  KE: { template: "USExclusiveLanding", bioLayout: null, language: "en-KE" },
  KR: { template: "USExclusiveLanding", bioLayout: null, language: "ko-KR" },
  KW: { template: "USExclusiveLanding", bioLayout: null, language: "ar-KW" },
  KZ: { template: "USExclusiveLanding", bioLayout: null, language: "ru-KZ" },
  LB: { template: "USExclusiveLanding", bioLayout: null, language: "ar-LB" },
  LT: { template: "USExclusiveLanding", bioLayout: null, language: "lt-LT" },
  LU: { template: "USExclusiveLanding", bioLayout: null, language: "fr-LU" },
  LV: { template: "USExclusiveLanding", bioLayout: null, language: "lv-LV" },
  MA: { template: "USExclusiveLanding", bioLayout: null, language: "ar-MA" },
  MD: { template: "USExclusiveLanding", bioLayout: null, language: "ro-MD" },
  ME: { template: "USExclusiveLanding", bioLayout: null, language: "sr-ME" },
  MK: { template: "USExclusiveLanding", bioLayout: null, language: "mk-MK" },
  MT: { template: "USExclusiveLanding", bioLayout: null, language: "mt-MT" },
  MX: { template: "USExclusiveLanding", bioLayout: null, language: "es-MX" },
  MY: { template: "USExclusiveLanding", bioLayout: null, language: "ms-MY" },
  NG: { template: "USExclusiveLanding", bioLayout: null, language: "en-NG" },
  NI: { template: "USExclusiveLanding", bioLayout: null, language: "es-NI" },
  NL: { template: "USExclusiveLanding", bioLayout: null, language: "nl-NL" },
  NO: { template: "USExclusiveLanding", bioLayout: null, language: "nb-NO" },
  NZ: { template: "USExclusiveLanding", bioLayout: null, language: "en-NZ" },
  OM: { template: "USExclusiveLanding", bioLayout: null, language: "ar-OM" },
  PA: { template: "USExclusiveLanding", bioLayout: null, language: "es-PA" },
  PE: { template: "USExclusiveLanding", bioLayout: null, language: "es-PE" },
  PH: { template: "USExclusiveLanding", bioLayout: null, language: "en-PH" },
  PK: { template: "USExclusiveLanding", bioLayout: null, language: "ur-PK" },
  PL: { template: "USExclusiveLanding", bioLayout: null, language: "pl-PL" },
  PR: { template: "USExclusiveLanding", bioLayout: null, language: "es-PR" },
  PT: { template: "USExclusiveLanding", bioLayout: null, language: "pt-PT" },
  PY: { template: "USExclusiveLanding", bioLayout: null, language: "es-PY" },
  QA: { template: "USExclusiveLanding", bioLayout: null, language: "ar-QA" },
  RO: { template: "USExclusiveLanding", bioLayout: null, language: "ro-RO" },
  RS: { template: "USExclusiveLanding", bioLayout: null, language: "sr-RS" },
  RU: { template: "USExclusiveLanding", bioLayout: null, language: "ru-RU" },
  SA: { template: "USExclusiveLanding", bioLayout: null, language: "ar-SA" },
  SE: { template: "USExclusiveLanding", bioLayout: null, language: "sv-SE" },
  SG: { template: "USExclusiveLanding", bioLayout: null, language: "en-SG" },
  SI: { template: "USExclusiveLanding", bioLayout: null, language: "sl-SI" },
  SK: { template: "USExclusiveLanding", bioLayout: null, language: "sk-SK" },
  SV: { template: "USExclusiveLanding", bioLayout: null, language: "es-SV" },
  TH: { template: "USExclusiveLanding", bioLayout: null, language: "th-TH" },
  TN: { template: "USExclusiveLanding", bioLayout: null, language: "ar-TN" },
  TR: { template: "USExclusiveLanding", bioLayout: null, language: "tr-TR" },
  TT: { template: "USExclusiveLanding", bioLayout: null, language: "en-TT" },
  TW: { template: "USExclusiveLanding", bioLayout: null, language: "zh-TW" },
  UA: { template: "USExclusiveLanding", bioLayout: null, language: "uk-UA" },
  US: { template: "USExclusiveLanding", bioLayout: null, language: "en-US" },
  UY: { template: "USExclusiveLanding", bioLayout: null, language: "es-UY" },
  VE: { template: "USExclusiveLanding", bioLayout: null, language: "es-VE" },
  VN: { template: "USExclusiveLanding", bioLayout: null, language: "vi-VN" },
  ZA: { template: "USExclusiveLanding", bioLayout: null, language: "en-ZA" },
};

/**
 * Política binaria de idioma — espejo histórico de `normalizeBinaryLanguage` en
 * cloudflare-worker-juaperrone/worker.js. `es-*` se conserva; todo lo demás →
 * `en-US`. Se mantiene por compatibilidad; el worker es ahora la fuente de verdad
 * para el multilingüe (ver normalizeSupportedLanguageClient).
 */
export function normalizeBinaryLanguage(lang: string | null | undefined): string {
  if (typeof lang !== "string" || !lang) return "en-US";
  return lang.toLowerCase().startsWith("es") ? lang : "en-US";
}

/**
 * Locales que el bundle i18n del front sabe renderizar — espejo de
 * SUPPORTED_LANG_BASES en worker.js. Para el override de debug `?country=XX` y
 * tests preservamos el tag nativo de estos idiomas (así `?country=DE` rinde de-DE
 * para QA); cualquier otro → `en-US`. NOTA: esto NO replica el gating por flag del
 * worker — los visitantes reales consumen `__TEMPLATE_CONFIG__.language` que el
 * worker ya resolvió (y gateó). Esta función es sólo el fallback debug/sin-worker.
 */
const SUPPORTED_LANG_BASES_CLIENT = ["es", "en", "pt", "de", "fr", "it"];
export function normalizeSupportedLanguageClient(lang: string | null | undefined): string {
  if (typeof lang !== "string" || !lang) return "en-US";
  const base = lang.toLowerCase().slice(0, 2);
  return SUPPORTED_LANG_BASES_CLIENT.includes(base) ? lang : "en-US";
}

/**
 * Configuración alineada con el Worker para overrides `?country=XX` (debug) y tests.
 */
export function getWorkerTemplateConfigForCountry(country: string): ServerTemplateConfig {
  const code = country.toUpperCase().trim().slice(0, 2);
  const row = WORKER_COUNTRY_TEMPLATE_MAP[code] ?? WORKER_DEFAULT_TEMPLATE;
  return {
    country: code,
    templateName: row.template,
    bioLayout: row.bioLayout,
    language: normalizeSupportedLanguageClient(row.language),
    province: null,
    cfCountry: code,
    timezone: "",
    city: "",
    detectionMethod: "manual_override_worker_map",
  };
}
